import { useMemo } from 'react';
import { useFinanceLoans } from '@/hooks/queries';
import { loanRows, loansUnreadyMessage } from '@/lib/loansDebtsRead';

// The "repays loan" picker, shared by every expenditure write surface (manual add,
// bank-classify, edit). The mirror of SettlesDebtPicker: selecting a loan tags the expenditure
// with its LoanId, so repaying a loan is the ordinary "money out" gesture pointed at the
// obligation it pays down. Orthogonal to the expenditure's category everywhere except Louisiana,
// where it decides the schedule.
//
// Why it is not merely convenient: Louisiana files a repayment as a Schedule B payment
// sub-record BENEATH the loan it repays, and derives that loan's closing balance from these
// links. An unlinked repayment therefore has no loan to sit under and cannot be reported at all —
// filing hard-fails rather than quietly leaving it off. So this control is how a Louisiana
// treasurer makes a repayment filable, not a nicety.
//
// Renders NOTHING when there are no active loans to point at, so a campaign that has never
// borrowed never sees an empty control. Loans still carrying a balance lead; repaid ones are
// listed but de-emphasized, because a late or correcting repayment is possible. The backend
// guards against repaying more than the principal.

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

interface RepaysLoanPickerProps {
  /** Currently-selected loan id, or null for "not a loan repayment". */
  value: string | null;
  onChange: (loanId: string | null) => void;
  /** Optional id for the <select> so a <label htmlFor> can point at it. */
  id?: string;
  /** Render just the bare <select> (no label wrapper) — for a surface that supplies its own
   *  label, e.g. the ledger detail grid. */
  hideLabel?: boolean;
}

export function RepaysLoanPicker({ value, onChange, id, hideLabel = false }: RepaysLoanPickerProps) {
  const loansRead = useFinanceLoans(undefined);
  const loans = loanRows(loansRead);
  const unready = loansUnreadyMessage(loansRead);

  const active = useMemo(
    () =>
      loans
        .filter((l) => l.voidedAtUtc === null)
        // Still-owed first, then repaid — stable within each bucket.
        .sort((a, b) => Number(b.outstandingBalance !== 0) - Number(a.outstandingBalance !== 0)),
    [loans],
  );

  if (unready) {
    return (
      <p role="alert" className="text-xs text-red-600">
        {unready}
      </p>
    );
  }
  if (active.length === 0) return null;

  const select = (
    <select
      id={id}
      aria-label="Repays loan"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      className="w-full rounded-md border px-3 py-2 text-sm"
    >
      <option value="">— Not a loan repayment —</option>
      {active.map((l) => (
        <option key={l.id} value={l.id}>
          {l.contactName ?? 'Lender'} — {money(l.amount)} loan
          {l.outstandingBalance !== 0 ? ` (${money(l.outstandingBalance)} owed)` : ' (repaid)'}
        </option>
      ))}
    </select>
  );

  if (hideLabel) return select;

  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium">Repays loan (optional)</span>
      {select}
    </label>
  );
}
